import type { CheckoutPayload } from '../../types/api';
import type { AddressSnapshot, AdminOrderRequest, Order, OrderRequest, OrderRequestStatus, UserAddress } from '../../types';
import { toStoredCheckoutConsents } from '../../types/consents';
import { addressSnapshotFromUserAddress } from '../../utils/address';
import { validateCancellationReason } from '../../utils/orderRequests';
import { delay, mockStorageKeys, readJson, writeJson } from '../mock/storage';
import type { IyzicoOrderRequest } from '../../types/payment';

const MOCK_SHIPPING_CARRIER_NAME = 'Yurtiçi Kargo';

function nextOrderNumber(): number {
  const current = readJson<number>(mockStorageKeys.nextOrderNumber, 1000);
  const next = current + 1;
  writeJson(mockStorageKeys.nextOrderNumber, next);
  return next;
}

function enrichOrder(order: Order): Order {
  return {
    ...order,
    shippingCarrierName: order.shippingCarrierName ?? MOCK_SHIPPING_CARRIER_NAME,
  };
}

export async function mockFetchOrders(userId: string): Promise<Order[]> {
  await delay(200);

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  return orders
    .filter((o) => o.userId === userId)
    .map(enrichOrder)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function readMockAddresses(userId: string): UserAddress[] {
  return readJson<Array<UserAddress & { userId: string }>>(mockStorageKeys.addresses, [])
    .filter((address) => address.userId === userId)
    .map(({ userId: _userId, ...address }) => address);
}

function resolveAddressSnapshot(
  userId: string,
  inline?: AddressSnapshot,
  addressId?: string | null,
): AddressSnapshot {
  if (addressId) {
    const saved = readMockAddresses(userId).find((address) => address.id === addressId);
    if (saved) {
      return addressSnapshotFromUserAddress(saved);
    }
  }

  if (inline) {
    return inline;
  }

  throw new Error('Adres bilgisi eksik.');
}

function isCheckoutPayload(payload: CheckoutPayload | IyzicoOrderRequest): payload is CheckoutPayload {
  return 'name' in payload.contact;
}

function buildOrderFromRequest(userId: string, payload: CheckoutPayload | IyzicoOrderRequest): Order {
  const contactName = isCheckoutPayload(payload)
    ? payload.contact.name
    : `${payload.contact.firstName} ${payload.contact.lastName}`.trim();

  const billingSameAsShipping = payload.billingSameAsShipping ?? true;
  const shippingAddress = resolveAddressSnapshot(
    userId,
    payload.shippingAddress,
    payload.shippingAddressId,
  );
  const billingAddress = billingSameAsShipping
    ? shippingAddress
    : resolveAddressSnapshot(userId, payload.billingAddress, payload.billingAddressId);

  const items = isCheckoutPayload(payload)
    ? payload.items
    : payload.items.map((item) => ({
        productId: item.productId,
        productName: '',
        productImage: '',
        quantity: item.quantity,
        price: item.price,
        currency: payload.currency,
      }));

  return {
    id: crypto.randomUUID(),
    orderNumber: nextOrderNumber(),
    userId,
    items,
    subtotal: payload.subtotal,
    discountAmount: payload.discountAmount ?? 0,
    couponCode: payload.couponCode ?? null,
    shipping: payload.shipping,
    total: payload.total,
    currency: payload.currency,
    status: 'pending',
    paymentStatus: 'completed',
    paymentProvider: 'iyzico',
    createdAt: new Date().toISOString(),
    shippingAddress,
    billingSameAsShipping,
    billingAddress,
    contact: {
      name: contactName,
      email: payload.contact.email,
      phone: payload.contact.phone,
      identityNumber: isCheckoutPayload(payload)
        ? undefined
        : payload.contact.identityNumber,
    },
    checkoutConsents: toStoredCheckoutConsents(payload.consents),
    shippingCarrierName: MOCK_SHIPPING_CARRIER_NAME,
  };
}

export async function mockCreateOrder(userId: string, payload: CheckoutPayload): Promise<Order> {
  await delay();

  if (
    !payload.consents.preInformationAccepted ||
    !payload.consents.distanceSalesAccepted ||
    !payload.consents.returnPolicyAccepted
  ) {
    throw new Error('Sipariş için gerekli sözleşme onayları verilmelidir.');
  }

  const order = enrichOrder(buildOrderFromRequest(userId, payload));

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  writeJson(mockStorageKeys.orders, [order, ...orders]);

  return order;
}

export async function mockCreateOrderFromIyzico(userId: string, payload: IyzicoOrderRequest): Promise<Order> {
  await delay();

  if (
    !payload.consents.preInformationAccepted ||
    !payload.consents.distanceSalesAccepted ||
    !payload.consents.returnPolicyAccepted
  ) {
    throw new Error('Sipariş için gerekli sözleşme onayları verilmelidir.');
  }

  const order = enrichOrder(buildOrderFromRequest(userId, payload));
  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  writeJson(mockStorageKeys.orders, [order, ...orders]);
  return order;
}

export async function mockGetOrder(userId: string, orderId: string): Promise<Order | null> {
  await delay(150);
  const orders = await mockFetchOrders(userId);
  return orders.find((o) => o.id === orderId) ?? null;
}

export async function mockFetchAllOrders(): Promise<Order[]> {
  await delay(250);
  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  return orders
    .map(enrichOrder)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export async function mockUpdateOrderStatus(orderId: string, status: Order['status']): Promise<Order> {
  await delay(200);
  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const index = orders.findIndex((o) => o.id === orderId);
  if (index === -1) {
    throw new Error('Sipariş bulunamadı.');
  }
  const updated = enrichOrder({ ...orders[index], status });
  orders[index] = updated;
  writeJson(mockStorageKeys.orders, orders);
  return updated;
}

export async function mockUpdateOrderTracking(
  orderId: string,
  trackingNumber: string | null,
): Promise<Order> {
  await delay(200);
  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const index = orders.findIndex((o) => o.id === orderId);
  if (index === -1) {
    throw new Error('Sipariş bulunamadı.');
  }
  const updated = enrichOrder({ ...orders[index], trackingNumber: trackingNumber || null });
  orders[index] = updated;
  writeJson(mockStorageKeys.orders, orders);
  return updated;
}

export async function mockUploadAdminOrderInvoice(orderId: string, file: File): Promise<Order> {
  await delay(300);

  const maxBytes = 10 * 1024 * 1024;
  const allowed = ['application/pdf', 'image/png', 'image/jpeg'];
  if (file.size > maxBytes) {
    throw new Error('Fatura en fazla 10 MB olabilir.');
  }
  if (!allowed.includes(file.type)) {
    throw new Error('Yalnızca PDF, PNG veya JPG yükleyebilirsiniz.');
  }

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const index = orders.findIndex((o) => o.id === orderId);
  if (index === -1) {
    throw new Error('Sipariş bulunamadı.');
  }
  const updated = enrichOrder({ ...orders[index], invoiceAvailable: true });
  orders[index] = updated;
  writeJson(mockStorageKeys.orders, orders);
  return updated;
}

export async function mockDownloadOrderInvoice(_orderId: string): Promise<void> {
  await delay(150);
  throw new Error('Mock modda fatura indirme desteklenmiyor.');
}

export async function mockDownloadAdminOrderInvoice(_orderId: string): Promise<void> {
  await delay(150);
  throw new Error('Mock modda fatura indirme desteklenmiyor.');
}

function findOrderIndex(orders: Order[], orderId: string, userId?: string) {
  return orders.findIndex((order) => order.id === orderId && (userId ? order.userId === userId : true));
}

export async function mockSubmitCancellationRequest(
  userId: string,
  orderId: string,
  reason: string,
): Promise<OrderRequest> {
  await delay(200);

  const validationError = validateCancellationReason(reason);
  if (validationError) {
    throw new Error(validationError);
  }

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const index = findOrderIndex(orders, orderId, userId);
  if (index === -1) {
    throw new Error('Sipariş bulunamadı.');
  }

  const order = orders[index];
  if (order.status !== 'pending' && order.status !== 'processing') {
    throw new Error('Bu sipariş için iptal talebi oluşturulamaz.');
  }

  const requests = order.requests ?? [];
  if (requests.some((request) => request.status === 'pending')) {
    throw new Error('Zaten bekleyen bir talep var.');
  }

  const request: OrderRequest = {
    id: crypto.randomUUID(),
    orderId,
    type: 'cancellation',
    status: 'pending',
    reason: reason.trim(),
    adminNote: null,
    createdAt: new Date().toISOString(),
    resolvedAt: null,
  };

  orders[index] = enrichOrder({ ...order, requests: [...requests, request] });
  writeJson(mockStorageKeys.orders, orders);
  return request;
}

export async function mockWithdrawOrderRequest(
  userId: string,
  orderId: string,
  requestId: string,
): Promise<void> {
  await delay(200);

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const index = findOrderIndex(orders, orderId, userId);
  if (index === -1) {
    throw new Error('Sipariş bulunamadı.');
  }

  const order = orders[index];
  const requests = order.requests ?? [];
  const requestIndex = requests.findIndex((request) => request.id === requestId);
  if (requestIndex === -1) {
    throw new Error('Talep bulunamadı.');
  }

  const request = requests[requestIndex];
  if (request.status !== 'pending') {
    throw new Error('İşlenmiş talep geri çekilemez.');
  }

  requests[requestIndex] = {
    ...request,
    status: 'withdrawn',
    resolvedAt: new Date().toISOString(),
  };
  orders[index] = enrichOrder({ ...order, requests: [...requests] });
  writeJson(mockStorageKeys.orders, orders);
}

export async function mockAdminCancelOrder(
  orderId: string,
  adminNote?: string | null,
): Promise<Order> {
  await delay(200);

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const index = findOrderIndex(orders, orderId);
  if (index === -1) {
    throw new Error('Sipariş bulunamadı.');
  }

  const order = orders[index];
  if (order.status !== 'pending' && order.status !== 'processing') {
    throw new Error('Bu sipariş iptal edilemez.');
  }

  const requests = (order.requests ?? []).map((request) =>
    request.type === 'cancellation' && request.status === 'pending'
      ? {
          ...request,
          status: 'approved' as const,
          adminNote: adminNote ?? request.adminNote ?? null,
          resolvedAt: new Date().toISOString(),
        }
      : request,
  );

  const updated = enrichOrder({ ...order, status: 'cancelled', requests });
  orders[index] = updated;
  writeJson(mockStorageKeys.orders, orders);
  return updated;
}

export async function mockFetchAdminOrderRequests(
  status?: OrderRequestStatus,
): Promise<AdminOrderRequest[]> {
  await delay(200);

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  const requests: AdminOrderRequest[] = [];

  for (const order of orders) {
    for (const request of order.requests ?? []) {
      if (status && request.status !== status) continue;
      requests.push({
        ...request,
        orderNumber: order.orderNumber,
        customerName: order.contact.name,
        customerEmail: order.contact.email,
        orderTotal: order.total,
        orderCurrency: order.currency,
      });
    }
  }

  return requests.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export async function mockUpdateAdminOrderRequest(
  requestId: string,
  status: 'approved' | 'rejected',
  adminNote?: string | null,
): Promise<AdminOrderRequest> {
  await delay(200);

  const orders = readJson<Order[]>(mockStorageKeys.orders, []);
  let updatedRequest: AdminOrderRequest | null = null;

  for (let index = 0; index < orders.length; index += 1) {
    const order = orders[index];
    const requests = order.requests ?? [];
    const requestIndex = requests.findIndex((request) => request.id === requestId);
    if (requestIndex === -1) continue;

    const request = requests[requestIndex];
    if (request.status !== 'pending') {
      throw new Error('Yalnızca bekleyen talepler güncellenebilir.');
    }

    const nextRequest: OrderRequest = {
      ...request,
      status,
      adminNote: adminNote ?? null,
      resolvedAt: new Date().toISOString(),
    };

    requests[requestIndex] = nextRequest;

    let nextOrder = order;
    if (status === 'approved' && request.type === 'cancellation') {
      nextOrder = { ...order, status: 'cancelled' };
    }

    orders[index] = enrichOrder({ ...nextOrder, requests: [...requests] });
    updatedRequest = {
      ...nextRequest,
      orderNumber: nextOrder.orderNumber,
      customerName: nextOrder.contact.name,
      customerEmail: nextOrder.contact.email,
      orderTotal: nextOrder.total,
      orderCurrency: nextOrder.currency,
    };
    break;
  }

  if (!updatedRequest) {
    throw new Error('Talep bulunamadı.');
  }

  writeJson(mockStorageKeys.orders, orders);
  return updatedRequest;
}
